import React, { useState, memo } from 'react';
import { MapPin, Lock, Unlock, Eye, User, Package } from 'lucide-react';
import { StcIntRelOrderDto } from '../../../../types/putaway';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PutAwayCardActionsProps {
  order: StcIntRelOrderDto;
  isOwned: boolean;
  onStartPutAway: (order: StcIntRelOrderDto) => Promise<void>;
  onLockOrder: (stcOrderNo: number) => Promise<void>;
  onUnlockOrder: (stcOrderNo: number) => Promise<void>;
  onPreviewOrder: (stcOrderNo: number) => void;
}

// Separate component for actions to isolate loading states
export const PutAwayCardActions: React.FC<PutAwayCardActionsProps> = memo(({
  order,
  isOwned,
  onStartPutAway,
  onLockOrder,
  onUnlockOrder,
  onPreviewOrder
}) => {
  const { t } = useTranslation();
  const [startingOrder, setStartingOrder] = useState(false);
  const [lockingOrder, setLockingOrder] = useState(false);

  const handleStartPutAway = async () => {
    try {
      setStartingOrder(true);
      await onStartPutAway(order);
    } catch (err) {
      console.error('Error starting put-away:', err);
    } finally {
      setStartingOrder(false);
    }
  };

  const handleLockOrder = async () => {
    try {
      setLockingOrder(true);
      await onLockOrder(order.stcOrderNo);
    } catch (err) {
      console.error('Error locking order:', err);
    } finally {
      setLockingOrder(false);
    }
  };

  const handleUnlockOrder = async () => {
    try {
      setLockingOrder(true);
      await onUnlockOrder(order.stcOrderNo);
    } catch (err) {
      console.error('Error unlocking order:', err);
    } finally {
      setLockingOrder(false);
    }
  };

  return (
    <div className="flex gap-1 sm:gap-2">
      <button
        onClick={handleStartPutAway}
        disabled={startingOrder}
        className="flex items-center gap-1 bg-green-600 text-white px-2 py-1.5 rounded text-xs hover:bg-green-700 transition-colors disabled:opacity-50 min-w-[50px] min-h-[36px] sm:min-w-[60px] sm:min-h-[40px] justify-center"
      >
        {startingOrder ? (
          <>
            <div className="w-2.5 h-2.5 animate-spin border border-white border-t-transparent rounded-full" />
          </>
        ) : (
          <>
            <MapPin size={12} className="sm:w-3 sm:h-3" />
            <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.start')}</span>
            <span className="sm:hidden">▶</span>
          </>
        )}
      </button>

      {isOwned ? (
        <button
          onClick={handleUnlockOrder}
          disabled={lockingOrder}
          className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1.5 rounded text-xs hover:bg-red-200 transition-colors disabled:opacity-50 min-w-[50px] min-h-[36px] sm:min-w-[60px] sm:min-h-[40px] justify-center"
        >
          {lockingOrder ? (
            <>
              <div className="w-2.5 h-2.5 animate-spin border border-red-500 border-t-transparent rounded-full" />
            </>
          ) : (
            <>
              <Unlock size={12} className="sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.unlock')}</span>
              <span className="sm:hidden">🔓</span>
            </>
          )}
        </button>
      ) : (
        <button
          onClick={handleLockOrder}
          disabled={lockingOrder}
          className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1.5 rounded text-xs hover:bg-green-200 transition-colors disabled:opacity-50 min-w-[60px] min-h-[36px] sm:min-w-[80px] sm:min-h-[40px] justify-center"
        >
          {lockingOrder ? (
            <>
              <div className="w-2.5 h-2.5 animate-spin border border-green-500 border-t-transparent rounded-full" />
            </>
          ) : (
            <>
              <Lock size={12} className="sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.takePutaway')}</span>
              <span className="sm:hidden">{t('warehouse.modules.putaway.actions.take')}</span>
            </>
          )}
        </button>
      )}

      <button
        onClick={() => onPreviewOrder(order.stcOrderNo)}
        className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1.5 rounded text-xs hover:bg-gray-200 transition-colors min-w-[40px] min-h-[36px] sm:min-w-[50px] sm:min-h-[40px] justify-center"
      >
        <Eye size={12} className="sm:w-3 sm:h-3" />
        <span className="hidden sm:inline">{t('warehouse.modules.putaway.actions.view')}</span>
        <span className="sm:hidden">👁</span>
      </button>
    </div>
  );
});

PutAwayCardActions.displayName = 'PutAwayCardActions';

interface PutAwayCardProps {
  order: StcIntRelOrderDto;
  isOwned: boolean;
  onStartPutAway: (order: StcIntRelOrderDto) => Promise<void>;
  onLockOrder: (stcOrderNo: number) => Promise<void>;
  onUnlockOrder: (stcOrderNo: number) => Promise<void>;
  onPreviewOrder: (stcOrderNo: number) => void;
}

// Main card component with memoization
export const PutAwayCard: React.FC<PutAwayCardProps> = memo(({
  order,
  isOwned,
  onStartPutAway,
  onLockOrder,
  onUnlockOrder,
  onPreviewOrder
}) => {
  const { t } = useTranslation();
  
  const getPriorityColor = (priority: number) => {
    if (priority >= 900) return 'bg-red-500';
    if (priority >= 500) return 'bg-orange-500';
    return 'bg-green-500';
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 0: return 'bg-gray-400';     // Ny
      case 10: return 'bg-green-500';   // Tildelt
      case 20: return 'bg-blue-500';    // Under arbeid
      case 30: return 'bg-yellow-500';  // Pauset
      case 100: return 'bg-green-600';  // Ferdig
      case 110: return 'bg-red-500';    // Avbrutt
      default: return 'bg-gray-400';    // Default
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0: return t('warehouse.modules.putaway.status.new');
      case 10: return t('warehouse.modules.putaway.status.assigned');
      case 20: return t('warehouse.modules.putaway.status.inProgress');
      case 30: return t('warehouse.modules.putaway.status.paused');
      case 100: return t('warehouse.modules.putaway.status.completed');
      case 110: return t('warehouse.modules.putaway.status.cancelled');
      default: return `${t('warehouse.modules.putaway.status.status')} ${status}`;
    }
  };

  const getTransTypeText = (transType: number) => {
    switch (transType) {
      case 0: return t('warehouse.modules.putaway.transTypes.internalMovement');
      case 1: return t('warehouse.modules.putaway.transTypes.pickToPacking');
      case 2: return t('warehouse.modules.putaway.transTypes.relocation');
      case 3: return t('warehouse.modules.putaway.transTypes.adjustment');
      default: return `${t('warehouse.modules.putaway.transTypes.type')} ${transType}`;
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border-2 p-2 sm:p-3 transition-all duration-200 ${
        isOwned 
          ? 'border-green-200 bg-green-50 hover:shadow-md hover:border-green-300' 
          : 'border-gray-200 hover:shadow-md hover:border-gray-300'
      }`}
    >
      {/* Mobile-first responsive layout */}
      <div className="space-y-2 sm:space-y-0">
        {/* Top row: Status dot + Order ID + Priority + Line count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${getStatusColor(order.orderStatus)}`} />
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 flex-shrink-0">
              #{order.stcOrderNo}
            </h3>
            {/* Description - truncated on mobile */}
            <div className="flex-1 min-w-0 px-1 sm:px-2">
              <div className="text-xs sm:text-sm text-gray-600 truncate">
                <span className="font-medium">{order.description1 || '-'}</span>
              </div>
            </div>
          </div>
          
          {/* Right side info - always visible but compact */}
          <div className="flex items-center gap-1 sm:gap-2 text-xs text-gray-600 whitespace-nowrap flex-shrink-0">
            <Package size={12} className="text-gray-400" />
            <span className="font-medium">{order.lineCount || 0}</span>
            <span className="text-gray-500">P:{order.orderPriority}</span>
          </div>
        </div>

        {/* Second row: Additional info (mobile) / inline (desktop) */}
        <div className="flex items-center justify-between text-xs text-gray-500 sm:hidden">
          <div className="truncate">
            {getTransTypeText(order.transType)}
            {order.warehouseID && (
              <span className="ml-1">| {t('warehouse.general.warehouse')}: {order.warehouseID}</span>
            )}
          </div>
        </div>

        {/* Actions row */}
        <div className="flex items-center justify-end">
          <PutAwayCardActions
            order={order}
            isOwned={isOwned}
            onStartPutAway={onStartPutAway}
            onLockOrder={onLockOrder}
            onUnlockOrder={onUnlockOrder}
            onPreviewOrder={onPreviewOrder}
          />
        </div>

        {/* Desktop additional info - hidden on mobile */}
        <div className="hidden sm:block text-xs text-gray-500 mt-1">
          {getTransTypeText(order.transType)}
          {order.warehouseID && (
            <span className="ml-2">| {t('warehouse.general.warehouse')}: {order.warehouseID}</span>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Only re-render if essential props change, not loading states
  return (
    prevProps.order.stcOrderNo === nextProps.order.stcOrderNo &&
    prevProps.order.orderStatus === nextProps.order.orderStatus &&
    prevProps.order.lockedBy === nextProps.order.lockedBy &&
    prevProps.isOwned === nextProps.isOwned &&
    prevProps.order.orderPriority === nextProps.order.orderPriority &&
    prevProps.order.description1 === nextProps.order.description1 &&
    prevProps.order.lineCount === nextProps.order.lineCount
  );
});

PutAwayCard.displayName = 'PutAwayCard';
